import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import { Config, FeRecordType, ListAction } from "config/config";
import { getRecordConfig } from "config/resourceUtils";
import { ActionToolbarProps } from "containers/Resources/components/CommonActions/ActionToolbar";
import { MoveToLocationAction } from "containers/Resources/components/CommonActions/CommonActions";
import {
    deleteKey,
    deleteProcessKey,
    restoreKey,
    restoreProcessKey,
} from "containers/Resources/components/CommonActions/constants";
import { Fragment, useMemo } from "react";

interface FilterActionsParams {
    deletedOnly?: boolean;
    normalOnly?: boolean;
}

const filterActionsForDeletedItems = (
    actions: ListAction[],
    { deletedOnly, normalOnly }: FilterActionsParams
) => {
    if (deletedOnly) {
        return actions.filter(({ key }) => key !== deleteKey && key !== deleteProcessKey);
    }
    if (normalOnly) {
        return actions.filter(({ key }) => key !== restoreKey && key !== restoreProcessKey);
    }
    return actions;
};

export default function ActionToolbar<T>({
    recordTypes,
    selectedItems,
    afterButtonAction,
    filterParams,
}: ActionToolbarProps<T>) {
    const executionConfig: Config = getRecordConfig(recordTypes as FeRecordType);
    const { isTopLevel, ...restFilterParams } = filterParams || {};

    const topLevelListActions = useMemo(
        () =>
            filterActionsForDeletedItems(
                [...(executionConfig?.listActions?.topLevel ?? [])],
                restFilterParams
            ),
        [executionConfig?.listActions?.topLevel, restFilterParams]
    );
    const allLevelListActions = useMemo(
        () =>
            filterActionsForDeletedItems(
                [...(executionConfig?.listActions?.allLevel ?? [])],
                restFilterParams
            ),
        [executionConfig?.listActions?.allLevel, restFilterParams]
    );
    const hiddenListActions = useMemo(
        () =>
            filterActionsForDeletedItems(
                [...(executionConfig?.listActions?.hidden ?? [])],
                restFilterParams
            ),
        [executionConfig?.listActions?.hidden, restFilterParams]
    );
    const shownListActions = useMemo(
        () =>
            filterActionsForDeletedItems(
                [...(executionConfig?.listActions?.shown ?? [])],
                restFilterParams
            ),
        [executionConfig?.listActions?.shown, restFilterParams]
    );

    const shownActions = useMemo(() => {
        const topLevelActions = (isTopLevel ? topLevelListActions : allLevelListActions) ?? [];

        return [...(shownListActions ?? []), ...topLevelActions];
    }, [allLevelListActions, shownListActions, topLevelListActions, isTopLevel]);

    const hiddenActions = useMemo(() => {
        const hiddenActions = (isTopLevel ? allLevelListActions : topLevelListActions) ?? [];
        return [...hiddenActions, ...(hiddenListActions ?? [])];
    }, [allLevelListActions, hiddenListActions, topLevelListActions, isTopLevel]);

    return (
        <div className="actionToolbar">
            {shownActions.map((action: any) => (
                <Fragment key={`action${action?.key}`}>
                    <action.action resources={selectedItems} afterAction={afterButtonAction} />
                </Fragment>
            ))}
            {hiddenActions.length > 0 && (
                <Popover
                    anchorOrigin={{
                        vertical: 10,
                        horizontal: 10,
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    trigger={
                        <Button icon={IconCollection.dotsHorizontal} variation="clear" label="" />
                    }
                >
                    <ul className="popoverMenu">
                        {hiddenActions.map((action: any) => (
                            <li key={`action${action.key}`}>
                                {action.action && (
                                    <action.action
                                        resources={selectedItems}
                                        afterAction={afterButtonAction}
                                        label={action.label}
                                    />
                                )}
                            </li>
                        ))}
                        <MoveToLocationAction
                            resources={selectedItems}
                            afterAction={afterButtonAction}
                        />
                    </ul>
                </Popover>
            )}
        </div>
    );
}
